import { AnalyticsService } from '@alison/analytics/client';
import { Button } from '@alison/ui';
import AddOutlined from '@mui/icons-material/AddOutlined';
import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { type MouseEvent, type ReactNode, useState } from 'react';
import { type DateWindow, usePanelValues } from '../../lib/filters.hooks';
import type { BoardName } from '../../lib/media-search';
import { ScopeLeadingKpiControl } from '../filters/LeadingKpiControl';
import {
  addFilterButtonSx,
  FILTER_BAR_ROW_SX,
  FILTER_MENU_SLOT_PROPS,
  filterMenuItemSx,
} from '../filters/menuStyles';
import { ScopeLabel } from '../filters/ScopeLabel';
import { DateRangeControl, MultiScopeSelectControl } from '../filters/ScopeRow';
import { SingleSelectControl } from '../filters/SingleSelectControl';
import type { ScopeOption, ScopeState } from '../filters/types';
import { useScopeOptions } from '../filters/useScopeOptions';
import { DEFAULT_KPI, MEDIA_PANEL_KEYS } from './data/constants';
import type { MediaFilters, MediaKpi } from './data/types';
import { marketName } from './data/utils';

const OPTIONAL_FILTER_LABELS = {
  market: 'Market',
  objective: 'Objective',
  kpi: 'KPI',
} as const;

type OptionalFilter = keyof typeof OPTIONAL_FILTER_LABELS;

// Brand / platform / account keep one width whatever is picked, so the date control after them
// doesn't hop between wrapped rows; longer names ellipsize with the full text in a tooltip.
const SCOPE_CHIP_WIDTH = 200;

interface PanelBucket {
  values: string[];
  labels?: Record<string, string>;
}

interface MediaFilterBarProps {
  scope: ScopeState;
  onScopeChange: (scope: ScopeState) => void;
  filters: MediaFilters;
  onFiltersChange: (filters: MediaFilters) => void;
  dateWindow: DateWindow;
  accountIds: number[];
  kpiOptions: MediaKpi[];
  bar?: BoardName;
  leading?: ReactNode;
}

function toOptions(
  bucket: PanelBucket | undefined,
  rename?: (value: string) => string,
): ScopeOption[] {
  // The values arrive in whatever case the network reported, so a market can be offered twice (`us`
  // and `US`). One entry per label — the board reports one merged market, and the SDK's membership
  // filters fold case, so either spelling selects both.
  const byLabel = new Map<string, ScopeOption>();
  for (const value of bucket?.values ?? []) {
    const label = rename ? rename(value) : (bucket?.labels?.[value] ?? value);
    if (!byLabel.has(label)) byLabel.set(label, { id: value, label });
  }
  return [...byLabel.values()].sort((first, second) => first.label.localeCompare(second.label));
}

export function selectedLabels(options: ScopeOption[], ids: string[]): string[] {
  return ids.map((id) => options.find((option) => option.id === id)?.label ?? id);
}

export function MediaFilterBar({
  scope,
  onScopeChange,
  filters,
  onFiltersChange,
  dateWindow,
  accountIds,
  kpiOptions,
  bar = 'media',
  leading,
}: MediaFilterBarProps): ReactNode {
  const { brandOptions, platformOptions, accountOptions } = useScopeOptions(scope);
  const { data: panelValues } = usePanelValues(MEDIA_PANEL_KEYS, undefined, dateWindow, accountIds);
  const marketOptions = toOptions(panelValues?.country, marketName);
  const typeOptions = toOptions(panelValues?.creativeType);
  const objectiveOptions = toOptions(panelValues?.campaignObjective);
  const [added, setAdded] = useState<ReadonlySet<OptionalFilter>>(new Set());
  const [addAnchor, setAddAnchor] = useState<HTMLButtonElement | null>(null);

  // Scope (brand, platform, account, date) and type are always on; the rest stay behind "Add filter" until added or set.
  const inUse: Record<OptionalFilter, boolean> = {
    market: filters.countries.length > 0,
    objective: filters.objectives.length > 0,
    kpi: filters.kpi !== DEFAULT_KPI,
  };
  // The dashboard reads the scope's leading KPI instead of the board KPI filter.
  const usesLeadingKpi = bar === 'dashboard';
  const optional = (Object.keys(OPTIONAL_FILTER_LABELS) as OptionalFilter[]).filter(
    (key) => key !== 'kpi' || !usesLeadingKpi,
  );
  const shown = (key: OptionalFilter): boolean =>
    optional.includes(key) && (added.has(key) || inUse[key]);
  const addable = optional.filter((key) => !shown(key));

  const trackAndApply = (
    filter: string,
    options: ScopeOption[],
    values: string[],
    next: MediaFilters,
  ): void => {
    AnalyticsService.track('Filter - Changed', {
      bar,
      filter,
      values: selectedLabels(options, values),
    });
    onFiltersChange(next);
  };

  const remove = (key: OptionalFilter, cleared: MediaFilters): void => {
    const next = new Set(added);
    next.delete(key);
    setAdded(next);
    if (inUse[key]) onFiltersChange(cleared);
  };

  return (
    <Box sx={FILTER_BAR_ROW_SX} data-tour="filters">
      {leading}
      <ScopeLabel title="The data every panel on this board is calculated over." />
      {usesLeadingKpi ? (
        <ScopeLeadingKpiControl scope={scope} onChange={onScopeChange} accountIds={accountIds} />
      ) : null}
      <MultiScopeSelectControl
        label="Brand"
        showSingleOptionName
        placeholderLabel="All brands"
        width={SCOPE_CHIP_WIDTH}
        options={brandOptions}
        selected={scope.brand}
        onChange={(brand) => onScopeChange({ ...scope, brand })}
      />
      <MultiScopeSelectControl
        label="Platform"
        showSingleOptionName
        placeholderLabel="All platforms"
        width={SCOPE_CHIP_WIDTH}
        options={platformOptions}
        selected={scope.platform}
        onChange={(platform) => onScopeChange({ ...scope, platform })}
      />
      <MultiScopeSelectControl
        label="Ad Account"
        showSingleOptionName
        placeholderLabel="All ad accounts"
        width={SCOPE_CHIP_WIDTH}
        options={accountOptions}
        selected={scope.account}
        onChange={(account) => onScopeChange({ ...scope, account })}
      />
      <DateRangeControl
        value={scope.range}
        onChange={(range) => onScopeChange({ ...scope, range })}
        resolved={dateWindow}
      />
      <MultiScopeSelectControl
        label="Creative Type"
        placeholderLabel="All creative types"
        options={typeOptions}
        selected={filters.creativeTypes}
        onChange={(creativeTypes) =>
          trackAndApply('creativeTypes', typeOptions, creativeTypes, {
            ...filters,
            creativeTypes,
          })
        }
      />
      {shown('market') ? (
        <MultiScopeSelectControl
          label="Market"
          placeholderLabel="All markets"
          options={marketOptions}
          selected={filters.countries}
          onChange={(countries) =>
            trackAndApply('countries', marketOptions, countries, { ...filters, countries })
          }
          onRemove={() => remove('market', { ...filters, countries: [] })}
        />
      ) : null}
      {shown('objective') ? (
        <MultiScopeSelectControl
          label="Objective"
          placeholderLabel="All objectives"
          options={objectiveOptions}
          selected={filters.objectives}
          onChange={(objectives) =>
            trackAndApply('objectives', objectiveOptions, objectives, { ...filters, objectives })
          }
          onRemove={() => remove('objective', { ...filters, objectives: [] })}
        />
      ) : null}
      {shown('kpi') ? (
        <SingleSelectControl
          label="KPI"
          options={kpiOptions}
          value={filters.kpi}
          onChange={(kpi) => trackAndApply('kpi', kpiOptions, [kpi], { ...filters, kpi })}
          onRemove={() => remove('kpi', { ...filters, kpi: DEFAULT_KPI })}
        />
      ) : null}
      {addable.length === 0 ? null : (
        <>
          <Button
            variant="outlined"
            startIcon={<AddOutlined sx={{ fontSize: 14 }} />}
            onClick={(event: MouseEvent<HTMLButtonElement>) => setAddAnchor(event.currentTarget)}
            sx={addFilterButtonSx}
          >
            Add Filter
          </Button>
          <Menu
            anchorEl={addAnchor}
            open={addAnchor !== null}
            onClose={() => setAddAnchor(null)}
            slotProps={FILTER_MENU_SLOT_PROPS}
          >
            {addable.map((key) => (
              <MenuItem
                key={key}
                onClick={() => {
                  setAdded(new Set(added).add(key));
                  setAddAnchor(null);
                }}
                sx={filterMenuItemSx}
              >
                {OPTIONAL_FILTER_LABELS[key]}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </Box>
  );
}
